import type { PersistedTopicPostMetadata } from "./topic-post-metadata";
import { getTopicProblemSearchTerms } from "./topic-problems";
import type { TopicDefinition } from "./topic-taxonomy";

export interface TopicRelatedPost {
  id: string | number;
  metadata?: PersistedTopicPostMetadata | null;
  title?: string | null;
  titleKo?: string | null;
  businessName?: string | null;
  businessNameKo?: string | null;
  name?: string | null;
  content?: string | null;
  contentKo?: string | null;
  body?: string | null;
  description?: string | null;
  shortDescription?: string | null;
  summary?: string | null;
  summaryKo?: string | null;
  category?: string | null;
  categoryLabel?: string | null;
  suburb?: string | null;
  city?: string | null;
  state?: string | null;
  location?: string | null;
  schedule?: string | null;
  ageRange?: string | null;
  hostName?: string | null;
  hostNickname?: string | null;
  hostDisplayName?: string | null;
  commentCount?: number | null;
  likes?: number | null;
  views?: number | null;
  currentMembers?: number | null;
  postCount?: number | null;
  tags?: string[] | null;
  focusKeywords?: string[] | null;
  localTopic?: string | null;
  createdAt?: string | Date | null;
  created_at?: string | Date | null;
  publishedAt?: string | Date | null;
}

type ScoredPost<T extends TopicRelatedPost> = {
  post: T;
  matchTier: number;
  score: number;
};

export type TopicRelatedOptions = {
  topicStageId?: string | null;
  excludeId?: string | number | null;
  minimumKeywordScore?: number;
};

export type TopicConnectionKey =
  | "stages"
  | "questions"
  | "news"
  | "businesses"
  | "meetings";

export type TopicConnectionCounts = Record<TopicConnectionKey, number>;

export interface TopicConnectionSummaryItem {
  key: TopicConnectionKey;
  label: string;
  value: number;
  description: string;
  targetId: string;
  isActive: boolean;
}

export function getTopicSearchTerms(topic: TopicDefinition): string[] {
  const businessCategoryTerms =
    topic.businessCategoryId === "service" ? [] : [topic.businessCategoryId];
  const expertTerms = topic.expertCategories.flatMap((category) => [
    category,
    category.replaceAll("_", " "),
  ]);
  const terms = [
    topic.label,
    topic.labelEn,
    ...topic.keywords,
    ...getTopicProblemSearchTerms(topic.id),
    ...businessCategoryTerms,
    ...expertTerms,
  ]
    .map((term) => normalizeTerm(term))
    .filter((term) => term.length >= 2);

  return Array.from(new Set(terms));
}

export function scoreTopicRelatedPost(
  topic: TopicDefinition,
  post: TopicRelatedPost,
): number {
  const terms = getTopicSearchTerms(topic);
  const title = normalizeTerm(
    [
      post.title,
      post.titleKo,
      post.businessName,
      post.businessNameKo,
      post.name,
    ].join(" "),
  );
  const content = normalizeTerm(
    [
      post.content,
      post.contentKo,
      post.body,
      post.description,
      post.shortDescription,
      post.summary,
      post.summaryKo,
      post.category,
      post.categoryLabel,
      post.suburb,
      post.city,
      post.state,
      post.location,
      post.schedule,
      post.ageRange,
      post.hostName,
      post.hostNickname,
      post.hostDisplayName,
      post.localTopic,
      ...(post.tags ?? []),
      ...(post.focusKeywords ?? []),
    ].join(" "),
  );

  return terms.reduce((score, term) => {
    const titleScore = containsSearchTerm(title, term) ? 6 : 0;
    const contentScore = containsSearchTerm(content, term) ? 2 : 0;
    return score + titleScore + contentScore;
  }, 0);
}

function containsSearchTerm(text: string, term: string): boolean {
  // Short acronyms such as ART and AAT must match a complete token. A plain
  // substring match connected visa Topics to Family Mart and Jakarta news.
  if (/^[a-z0-9]+$/.test(term) && term.length <= 3) {
    return text.split(/[^a-z0-9]+/).includes(term);
  }

  return text.includes(term);
}

/**
 * Ranks real content by persisted metadata first and keyword relevance second.
 *
 * Match tiers:
 * 3. Same Topic and requested Stage
 * 2. Same Topic
 * 1. Keyword fallback
 *
 * Posts are deduplicated by ID and no placeholder/fake items are generated.
 * With no persisted metadata present, this preserves the existing keyword-only
 * behavior.
 */
export function getRelatedTopicPosts<T extends TopicRelatedPost>(
  topic: TopicDefinition,
  posts: readonly T[],
  limit = 5,
  options: TopicRelatedOptions = {},
): T[] {
  const seen = new Set<string>();
  const excludedId =
    options.excludeId === undefined || options.excludeId === null
      ? null
      : String(options.excludeId);
  const minimumKeywordScore = Math.max(1, options.minimumKeywordScore ?? 1);

  return posts
    .filter((post) => {
      const id = String(post.id);
      if (id === excludedId || seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map((post): ScoredPost<T> => {
      const score = scoreTopicRelatedPost(topic, post);
      return {
        post,
        matchTier: topicMetadataMatchTier(
          topic,
          post,
          options.topicStageId,
          score >= minimumKeywordScore ? score : 0,
        ),
        score,
      };
    })
    .filter((item) => item.matchTier > 0)
    .sort((a, b) => {
      if (b.matchTier !== a.matchTier) return b.matchTier - a.matchTier;
      if (b.score !== a.score) return b.score - a.score;

      const engagementA = engagementScore(a.post);
      const engagementB = engagementScore(b.post);
      if (engagementB !== engagementA) return engagementB - engagementA;

      return timestampMs(b.post) - timestampMs(a.post);
    })
    .slice(0, Math.max(0, limit))
    .map((item) => item.post);
}

export function buildTopicConnectionSummary(
  counts: TopicConnectionCounts,
): TopicConnectionSummaryItem[] {
  return [
    {
      key: "stages",
      label: "단계",
      value: counts.stages,
      description: "상황별 여정",
      targetId: "topic-stages",
      isActive: counts.stages > 0,
    },
    {
      key: "questions",
      label: "질문/경험",
      value: counts.questions,
      description: "사용자 사례",
      targetId: "topic-related-questions",
      isActive: counts.questions > 0,
    },
    {
      key: "news",
      label: "변화 신호",
      value: counts.news,
      description: "뉴스/정책",
      targetId: "topic-related-news",
      isActive: counts.news > 0,
    },
    {
      key: "businesses",
      label: "업체/전문가",
      value: counts.businesses,
      description: "해결 파트너",
      targetId: "topic-related-businesses",
      isActive: counts.businesses > 0,
    },
    {
      key: "meetings",
      label: "모임",
      value: counts.meetings,
      description: "같은 상황의 사람들",
      targetId: "topic-related-meetings",
      isActive: counts.meetings > 0,
    },
  ];
}

function topicMetadataMatchTier(
  topic: TopicDefinition,
  post: TopicRelatedPost,
  topicStageId: string | null | undefined,
  keywordScore: number,
): number {
  if (post.metadata?.topicId === topic.id) {
    if (topicStageId && post.metadata.topicStageId === topicStageId) return 3;
    return 2;
  }

  return keywordScore > 0 ? 1 : 0;
}

function engagementScore(post: TopicRelatedPost): number {
  return (
    (post.commentCount ?? 0) * 3 +
    (post.postCount ?? 0) * 3 +
    (post.likes ?? 0) * 2 +
    (post.currentMembers ?? 0) * 2 +
    (post.views ?? 0)
  );
}

function timestampMs(post: TopicRelatedPost): number {
  const value = post.publishedAt ?? post.createdAt ?? post.created_at;
  if (!value) return 0;

  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
}

function normalizeTerm(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}
